import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, AlertCircle, User, Building2, ArrowLeft, Loader2, FileCheck2, FileText, Receipt, Plus, Trash2, CheckCircle } from 'lucide-react';
import { extrairFaturaCrm, extrairDocumentoCrm } from '../../../src/services/crmExtracaoApi';
import { UCData } from './NewNegotiationWizard/StepUC';
import { mapFaturaDataToUC, mapExtracaoFaturaCrmToFaturaData } from './faturaExtraction';

export interface FaturaColetada {
    file: File;
    uc: Partial<UCData>;
}

export interface AutomaticoResult {
    tipoCliente: 'PF' | 'PJ';
    nome_cliente?: string;
    cpf_cnpj?: string;
    documentoFile?: File;
    documentoLabel?: string;
    faturas: FaturaColetada[];
}

interface NovaNegociacaoAutomaticaModalProps {
    isOpen: boolean;
    onClose: () => void;
    onReady: (result: AutomaticoResult) => void;
}

type Step = 'tipo' | 'documento' | 'fatura';

const NovaNegociacaoAutomaticaModal: React.FC<NovaNegociacaoAutomaticaModalProps> = ({ isOpen, onClose, onReady }) => {
    const [step, setStep] = useState<Step>('tipo');
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ' | null>(null);

    const [documentoFile, setDocumentoFile] = useState<File | null>(null);
    const [documentoData, setDocumentoData] = useState<Record<string, any> | null>(null);
    const [isUploadingDocumento, setIsUploadingDocumento] = useState(false);
    const [documentoError, setDocumentoError] = useState<string | null>(null);

    const [faturasColetadas, setFaturasColetadas] = useState<FaturaColetada[]>([]);
    const [showFaturaUploader, setShowFaturaUploader] = useState(true);
    const [faturaFile, setFaturaFile] = useState<File | null>(null);
    const [isUploadingFatura, setIsUploadingFatura] = useState(false);
    const [faturaError, setFaturaError] = useState<string | null>(null);
    const backdropMouseDownRef = useRef(false);

    const resetAll = () => {
        setStep('tipo');
        setTipoCliente(null);
        setDocumentoFile(null);
        setDocumentoData(null);
        setDocumentoError(null);
        setIsUploadingDocumento(false);
        setFaturasColetadas([]);
        setShowFaturaUploader(true);
        setFaturaFile(null);
        setFaturaError(null);
        setIsUploadingFatura(false);
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const handleDocumentoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
                setDocumentoFile(file);
                setDocumentoError(null);
            } else {
                setDocumentoError('Selecione um arquivo PDF ou uma foto (JPG/PNG) válida.');
                setDocumentoFile(null);
            }
        }
    };

    const handleFaturaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type === 'application/pdf') {
                setFaturaFile(file);
                setFaturaError(null);
            } else {
                setFaturaError('Por favor, selecione um arquivo PDF válido.');
                setFaturaFile(null);
            }
        }
    };

    const handleUploadDocumento = async () => {
        if (!documentoFile) {
            setDocumentoError('Selecione um arquivo para enviar.');
            return;
        }
        setIsUploadingDocumento(true);
        setDocumentoError(null);
        const resposta = await extrairDocumentoCrm(documentoFile, tipoCliente ?? undefined);
        setIsUploadingDocumento(false);
        if (!resposta.ok) {
            setDocumentoError(resposta.error || 'Não foi possível processar o documento enviado.');
            return;
        }
        // Melhor esforço (RG/CNH/CNPJ não têm layout único) — nome/CPF/tipo são só prefill
        // editável no próximo passo, então seguir mesmo com campos parcialmente vazios está OK.
        setDocumentoData({
            cliente_nome: resposta.cliente_nome,
            cpf_cnpj: resposta.cpf_cnpj,
            tipo_documento: resposta.tipo_documento,
        });
        setStep('fatura');
    };

    // Etapa de documento é opcional — o vendedor pode não ter o documento em mãos no momento.
    const handlePularDocumento = () => {
        setDocumentoFile(null);
        setDocumentoData(null);
        setDocumentoError(null);
        setStep('fatura');
    };

    const handleUploadFatura = async () => {
        if (!faturaFile) {
            setFaturaError('Selecione um arquivo PDF para enviar.');
            return;
        }
        setIsUploadingFatura(true);
        setFaturaError(null);

        const resposta = await extrairFaturaCrm(faturaFile);
        if (!resposta.ok) {
            setIsUploadingFatura(false);
            setFaturaError(resposta.error || 'Não foi possível processar a fatura enviada.');
            return;
        }
        // Regra de ouro do parser: nunca emitir número errado em silêncio — faturas que não
        // reconciliam viram 'falhou' e não devem preencher a UC com dado incerto.
        if (resposta.reconciliacao?.status === 'falhou') {
            const criticos = resposta.reconciliacao.checagens
                .filter((c) => c.tipo === 'critico')
                .map((c) => c.mensagem)
                .slice(0, 3)
                .join(' | ');
            setIsUploadingFatura(false);
            setFaturaError(`A fatura não pôde ser lida com confiança suficiente${criticos ? `: ${criticos}` : '.'}`);
            return;
        }
        if (!resposta.estruturado) {
            setIsUploadingFatura(false);
            setFaturaError('A fatura foi processada, mas nenhum dado foi retornado. Preencha os dados manualmente.');
            return;
        }
        setIsUploadingFatura(false);

        // Cria uma nova UC vinculada a esta negociação — cada fatura enviada no laço vira uma UC própria.
        const data = mapExtracaoFaturaCrmToFaturaData(resposta);
        setFaturasColetadas(prev => [...prev, { file: faturaFile, uc: mapFaturaDataToUC(data) as Partial<UCData> }]);
        setFaturaFile(null);
        setShowFaturaUploader(false);
    };

    const handleRemoverFatura = (index: number) => {
        setFaturasColetadas(prev => prev.filter((_, i) => i !== index));
    };

    const handleFinalizar = () => {
        if (!tipoCliente) return;
        onReady({
            tipoCliente,
            nome_cliente: documentoData?.cliente_nome,
            cpf_cnpj: documentoData?.cpf_cnpj,
            documentoFile: documentoFile || undefined,
            documentoLabel: documentoData?.tipo_documento || undefined,
            faturas: faturasColetadas,
        });
        resetAll();
    };

    // Etapa de fatura também é opcional — encerra o fluxo com o que já foi coletado até aqui (documento, se houver).
    const handlePularFatura = () => {
        if (!tipoCliente) return;
        onReady({
            tipoCliente,
            nome_cliente: documentoData?.cliente_nome,
            cpf_cnpj: documentoData?.cpf_cnpj,
            documentoFile: documentoFile || undefined,
            documentoLabel: documentoData?.tipo_documento || undefined,
            faturas: [],
        });
        resetAll();
    };

    if (!isOpen) return null;

    const tituloDocumento = tipoCliente === 'PJ'
        ? 'Envie o Contrato Social ou Cartão CNPJ da empresa'
        : 'Envie o documento de identificação (RG, CNH ou CPF)';

    return createPortal(
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
            onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => { if (backdropMouseDownRef.current) handleClose(); backdropMouseDownRef.current = false; }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 dark:text-white">Nova Negociação — Automático</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {step === 'tipo' && 'Passo 1 de 3'}
                            {step === 'documento' && 'Passo 2 de 3'}
                            {step === 'fatura' && 'Passo 3 de 3'}
                        </p>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    {step === 'tipo' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                                Selecione o tipo de cliente para continuar
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => { setTipoCliente('PF'); setStep('documento'); }}
                                    className="flex flex-col items-center gap-2 p-6 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                                >
                                    <User size={28} className="text-primary" />
                                    <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">Pessoa Física</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">PF</span>
                                </button>
                                <button
                                    onClick={() => { setTipoCliente('PJ'); setStep('documento'); }}
                                    className="flex flex-col items-center gap-2 p-6 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                                >
                                    <Building2 size={28} className="text-primary" />
                                    <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">Pessoa Jurídica</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">PJ</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'documento' && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-xl">
                                <div className="p-2.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-lg shrink-0">
                                    <FileText size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-blue-800 dark:text-blue-300 leading-tight">Enviar Documento</h3>
                                    <p className="text-xs text-blue-700/80 dark:text-blue-400/80">{tituloDocumento}</p>
                                </div>
                            </div>

                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative cursor-pointer">
                                <input
                                    type="file"
                                    accept="application/pdf,image/*"
                                    onChange={handleDocumentoFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 mb-4">
                                    <Upload size={32} />
                                </div>
                                {documentoFile ? (
                                    <div className="text-center">
                                        <p className="font-medium text-gray-800 dark:text-gray-200">{documentoFile.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {(documentoFile.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <p className="font-medium text-gray-700 dark:text-gray-300">Clique para selecionar um arquivo</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">PDF ou foto (JPG/PNG)</p>
                                    </div>
                                )}
                            </div>

                            {documentoError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm border border-red-100 dark:border-red-800">
                                    <AlertCircle size={16} className="shrink-0" />
                                    <p>{documentoError}</p>
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <button
                                    onClick={() => setStep('tipo')}
                                    disabled={isUploadingDocumento}
                                    className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    <ArrowLeft size={16} /> Voltar
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handlePularDocumento}
                                        disabled={isUploadingDocumento}
                                        className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        Pular esta etapa
                                    </button>
                                    <button
                                        onClick={handleUploadDocumento}
                                        disabled={!documentoFile || isUploadingDocumento}
                                        className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isUploadingDocumento ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" /> Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={18} /> Enviar
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 'fatura' && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/30 rounded-xl">
                                <div className="p-2.5 bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 rounded-lg shrink-0">
                                    <Receipt size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-orange-800 dark:text-orange-300 leading-tight">Enviar Fatura</h3>
                                    <p className="text-xs text-orange-700/80 dark:text-orange-400/80">Cada fatura enviada cria uma Unidade Consumidora vinculada a esta negociação</p>
                                </div>
                            </div>

                            {documentoData && faturasColetadas.length === 0 && (
                                <div className="flex items-center gap-2 p-2.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-xs border border-green-100 dark:border-green-800/30">
                                    <FileCheck2 size={14} className="shrink-0" />
                                    <p>Documento processado com sucesso.</p>
                                </div>
                            )}

                            {faturasColetadas.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                        Faturas adicionadas ({faturasColetadas.length})
                                    </p>
                                    {faturasColetadas.map((f, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 rounded-lg">
                                            <div className="flex items-center gap-2 text-sm min-w-0">
                                                <FileCheck2 size={16} className="text-green-600 dark:text-green-400 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-gray-800 dark:text-gray-100">UC {f.uc.numero_uc || '(sem número)'}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{f.file.name}</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleRemoverFatura(idx)}
                                                className="p-1.5 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
                                                title="Remover fatura"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {showFaturaUploader ? (
                                <>
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative cursor-pointer">
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            onChange={handleFaturaFileChange}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="p-4 bg-orange-100 dark:bg-orange-900/30 rounded-full text-orange-600 dark:text-orange-400 mb-4">
                                            <Upload size={32} />
                                        </div>
                                        {faturaFile ? (
                                            <div className="text-center">
                                                <p className="font-medium text-gray-800 dark:text-gray-200">{faturaFile.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    {(faturaFile.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <p className="font-medium text-gray-700 dark:text-gray-300">Clique para selecionar um arquivo</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Apenas arquivos PDF</p>
                                            </div>
                                        )}
                                    </div>

                                    {faturaError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm border border-red-100 dark:border-red-800">
                                            <AlertCircle size={16} className="shrink-0" />
                                            <p>{faturaError}</p>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <button
                                            onClick={() => faturasColetadas.length > 0 ? setShowFaturaUploader(false) : setStep('documento')}
                                            disabled={isUploadingFatura}
                                            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                                        >
                                            <ArrowLeft size={16} /> {faturasColetadas.length > 0 ? 'Cancelar' : 'Voltar'}
                                        </button>
                                        <div className="flex items-center gap-2">
                                            {faturasColetadas.length === 0 && (
                                                <button
                                                    onClick={handlePularFatura}
                                                    disabled={isUploadingFatura}
                                                    className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium transition-colors disabled:opacity-50"
                                                >
                                                    Pular e finalizar
                                                </button>
                                            )}
                                            <button
                                                onClick={handleUploadFatura}
                                                disabled={!faturaFile || isUploadingFatura}
                                                className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isUploadingFatura ? (
                                                    <>
                                                        <Loader2 size={18} className="animate-spin" /> Enviando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload size={18} /> Enviar
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center justify-between gap-3 mt-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <button
                                        onClick={() => setStep('documento')}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                                    >
                                        <ArrowLeft size={16} /> Voltar
                                    </button>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setShowFaturaUploader(true)}
                                            className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg font-medium transition-colors"
                                        >
                                            <Plus size={16} /> Adicionar Nova Fatura
                                        </button>
                                        <button
                                            onClick={handleFinalizar}
                                            className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors"
                                        >
                                            <CheckCircle size={18} /> Finalizar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NovaNegociacaoAutomaticaModal;
